import { createAction, handleActions, Action } from 'redux-actions';
import { useRedux } from 'util/hook/redux';
import { Dispatch } from 'redux';
import history from 'store/history';

import { api } from 'util/api';
import { EyeCheckImageListRequestParams } from 'util/api/swaggerApi/data-contracts';

import { updateAccessToken } from './auth';

import { GetState, State as GlobalState } from './reducers';

export interface EyesImageReportRecord {
	uid?: string;
	date?: string;
	dr?: string;
	description?: string;
	photo?: string[];
	video?: string[];
}

export interface EyesImageReportModalityItem {
	modality?: string;
	data?: {
		aps?: {
			url?: string;
			record?: EyesImageReportRecord[];
		};
	};
}

export interface MediaType {
	type: string;
	url: string;
	combineIndex: number;
}

export interface EyeCheckImageReportType {
	id: number;
	date: string;
	rawDate: string;
	description: string;
	doctorName: string;
	summary: string;
	reportBase64: string;
	photo: string[];
	institution: string;
	video: string[];
	combineMedia: MediaType[];
	groupedMedia: MediaType[][];
	/** viewer 虛擬目錄（eyes-image-report 各 modality 獨立） */
	imgUrl?: string;
	/** 影像類型代碼，如 ALSCAN1 */
	modality?: string;
}

function formatReportDate(date?: string): string {
	if (!date) {
		return '';
	}
	if (date.includes('/')) {
		return date;
	}
	if (/^\d{8}$/.test(date)) {
		return `${date.slice(0, 4)}/${date.slice(4, 6)}/${date.slice(6, 8)}`;
	}
	if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date.replace(/-/g, '/');
	}
	return `${date.slice(0, 4)}/${date.slice(5, 7)}/${date.slice(8)}`;
}

function parseDoctorInfo(dr?: string): { doctorName: string; institution: string } {
	if (!dr) {
		return { doctorName: '', institution: '' };
	}
	const splitDr = dr.split('(');
	return {
		doctorName: splitDr[0].trim(),
		institution: splitDr[1] ? splitDr[1].replace(')', '').trim() : '',
	};
}

function buildCombineMedia(record: EyesImageReportRecord): {
	combineMedia: MediaType[];
	groupedMedia: MediaType[][];
} {
	let combineMedia: MediaType[] = [
		...(record.photo?.map(photo => ({ type: 'photo', url: photo, combineIndex: 0 })) || []),
		...(record.video?.map(video => ({ type: 'video', url: video, combineIndex: 0 })) || []),
	];
	combineMedia = combineMedia.map((item, index) => ({ ...item, combineIndex: index }));

	const groupedMedia: MediaType[][] = [];
	for (let i = 0; i < combineMedia.length; i += 5) {
		groupedMedia.push(combineMedia.slice(i, i + 5));
	}

	return { combineMedia, groupedMedia };
}

interface EyesImageReportApiResponse {
	aps?: {
		url?: string;
		record?: Record<string, EyesImageReportRecord[] | null>;
	};
}

function transformApiResponseToModalityItems(apiData: EyesImageReportApiResponse): EyesImageReportModalityItem[] {
	if (!apiData?.aps?.record) {
		return [];
	}

	const items: EyesImageReportModalityItem[] = [];
	const url = apiData.aps.url || '';
	const record = apiData.aps.record;

	Object.entries(record).forEach(([modality, records]) => {
		if (records && Array.isArray(records)) {
			items.push({
				modality,
				data: {
					aps: {
						url,
						record: records as EyesImageReportRecord[],
					},
				},
			});
		}
	});

	return items;
}

export function processEyesImageReportList(items: EyesImageReportModalityItem[]): EyeCheckImageReportType[] {
	let id = 0;
	const reports: EyeCheckImageReportType[] = [];

	items.forEach(item => {
		const imgUrl = item.data?.aps?.url || '';
		const modality = item.modality || '';
		const records = item.data?.aps?.record || [];

		records.forEach(record => {
			const { doctorName, institution } = parseDoctorInfo(record.dr);
			const { combineMedia, groupedMedia } = buildCombineMedia(record);
			const description = record.description || modality;

			reports.push({
				id: id++, // eslint-disable-line no-plusplus
				date: formatReportDate(record.date),
				rawDate: record.date || '',
				description,
				doctorName,
				summary: description,
				reportBase64: '',
				photo: record.photo || [],
				institution,
				video: record.video || [],
				combineMedia,
				groupedMedia,
				imgUrl,
				modality,
			});
		});
	});

	reports.sort((a, b) => (b.rawDate || '').localeCompare(a.rawDate || ''));

	return reports;
}

export type EyeCheckImageListFetchOpts =
	| string
	| {
			date?: string;
			/** 眼科影像類型，空字串表示不篩選 */
			imageType?: string;
	  };

function normalizeEyeCheckImageFetch(opts: EyeCheckImageListFetchOpts): { date: string; imageType?: string } {
	if (typeof opts === 'string') {
		return { date: opts };
	}
	const imageType = opts.imageType?.trim() || undefined;
	return {
		date: opts.date ?? '',
		...(imageType ? { imageType } : {}),
	};
}

export const getEyeCheckImageListReportList = createAction(
	'GET_EYE_CHECK_IMAGE',
	(payload: EyeCheckImageListFetchOpts = '') =>
		async (dispatch: Dispatch, getState: GetState) => {
			const {
				user: {
					userInfo: { patientNo },
				},
			} = getState();

			const { date, imageType } = normalizeEyeCheckImageFetch(payload);

			const params: EyeCheckImageListRequestParams = {
				patientNo: patientNo || '',
				date,
				...(imageType ? { imageType } : {}),
			};

			let response;
			const { v1EyeReportList } = api;

			try {
				response = await v1EyeReportList(params);
			} catch (e) {
				console.log('image report error', e);
				const { status } = e as { status: number };
				if (status === 401) {
					dispatch(updateAccessToken(''));
					history.push('/');
				}
				return [];
			}

			const { data } = response;

			if (!data?.data) {
				return [];
			}
			return data?.data
			// return processedRecords;
		},
);

const useEyesImageReportMock = (): boolean => process.env.USE_EYES_IMAGE_REPORT_MOCK === true;

export const getEyesImageReportList = createAction(
	'GET_EYES_IMAGE_REPORT',
	(modality: string = '') =>
		async (dispatch: Dispatch) => {
		const modalityCode = modality.trim();

		if (useEyesImageReportMock()) {
			const { eyesImageReportMockModalityItems } = await import(
				/* webpackChunkName: 'eyesImageReportMock' */ 'models/mocks/eyesImageReportMock'
			);
			const items = modalityCode
				? eyesImageReportMockModalityItems.filter(item => item.modality === modalityCode)
				: eyesImageReportMockModalityItems;
			return processEyesImageReportList(items);
		}

		let response;
		const { v1UserEyesImageReportList } = api;

		try {
			response = await v1UserEyesImageReportList(
				modalityCode ? { modality: modalityCode } : {},
			);
		} catch (e) {
			console.log('eyes image report error', e);
			const { status } = e as { status: number };
			if (status === 401) {
				dispatch(updateAccessToken(''));
				history.push('/');
			}
			return [];
		}

		const { data } = response;

		if (!data?.data) {
			return [];
		}

		const modalityItems = transformApiResponseToModalityItems(data.data);
		return processEyesImageReportList(modalityItems);
	},
);

export interface State {
	loading: boolean;
	reports: EyeCheckImageReportType[];
}

export const defaultState: State = {
	loading: false,
	reports: [],
};

export const reducer = {
	// Workaround: HandleActions 目前定義無法支援多種 action 形式
	eyeCheckImageReports: handleActions<State, any>( // eslint-disable-line @typescript-eslint/no-explicit-any
		{
			GET_EYE_CHECK_IMAGE_PENDING: state => ({
				...state,
				loading: true,
			}),
			GET_EYE_CHECK_IMAGE_FULFILLED: (state, action: Action<EyeCheckImageReportType[]>) => ({
				...state,
				reports: action.payload,
				loading: false,
			}),
			GET_EYES_IMAGE_REPORT_PENDING: state => ({
				...state,
				loading: true,
			}),
			GET_EYES_IMAGE_REPORT_FULFILLED: (state, action: Action<EyeCheckImageReportType[]>) => ({
				...state,
				reports: action.payload,
				loading: false,
			}),
		},
		defaultState,
	),
};

const eyeCheckImageReportActionsMap = {
	getEyeCheckImageListReportList,
	getEyesImageReportList,
};

const mapHooksToState = (state: GlobalState) => ({
	eyeCheckImageReports: state.eyeCheckImageReports.reports,
	loading: state.eyeCheckImageReports.loading,
});

type EyeCheckImageReportSelector = ReturnType<typeof mapHooksToState>;
type EyeCheckImageReportActionsMap = typeof eyeCheckImageReportActionsMap;

export const useEyeCheckImageReport = () =>
	useRedux<EyeCheckImageReportSelector, EyeCheckImageReportActionsMap>(mapHooksToState, eyeCheckImageReportActionsMap);
